// Phase 0085-B — My Assessments list page.
// Lists assessments where the user is appraisee / appraiser / reviewer.

import { App, Button, Empty, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { assessmentApi, type AssessmentStage, type EmployeeAssessment } from '@/features/hr/assessmentApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const STAGE_COLOR: Record<string, string> = {
  DRAFT: 'default',
  SELF_IN_PROGRESS: 'blue',
  SELF_SUBMITTED: 'cyan',
  REPORTING_IN_PROGRESS: 'blue',
  REPORTING_SUBMITTED: 'cyan',
  REVIEWING_IN_PROGRESS: 'blue',
  REVIEWING_SUBMITTED: 'cyan',
  REPRESENTATION: 'orange',
  HR_REVIEW: 'volcano',
  LOCKED: 'green',
  REOPENED: 'gold',
}

export default function MyAssessmentsPage() {
  const navigate = useNavigate()

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['my-assessments'],
    queryFn: () => assessmentApi.mine(),
    refetchOnWindowFocus: true,
  })

  const columns: ColumnsType<EmployeeAssessment> = [
    { title: 'Employee', dataIndex: 'employee_id', width: 250,
      render: (id: string) => <Text code style={{ fontSize: 11 }}>{id.substring(0, 12)}…</Text> },
    { title: 'Stage', dataIndex: 'current_stage', width: 200,
      render: (s: AssessmentStage) => <Tag color={STAGE_COLOR[s]}>{s}</Tag> },
    { title: 'Self', dataIndex: 'self_submitted_at', width: 130,
      render: (d: string | null) => d ? dayjs(d).format('MMM D') : '—' },
    { title: 'Reporting', dataIndex: 'reporting_submitted_at', width: 130,
      render: (d: string | null) => d ? dayjs(d).format('MMM D') : '—' },
    { title: 'Reviewing', dataIndex: 'reviewing_submitted_at', width: 130,
      render: (d: string | null) => d ? dayjs(d).format('MMM D') : '—' },
    { title: 'Locked', dataIndex: 'hr_locked_at', width: 130,
      render: (d: string | null) => d ? dayjs(d).format('MMM D') : '—' },
    { title: 'Score', dataIndex: 'normalized_score_100', width: 110,
      render: (v: number | null, r) => v != null ? (
        <Space>
          <Text strong>{Number(v).toFixed(1)}</Text>
          {r.performance_grade && <Tag>{r.performance_grade}</Tag>}
        </Space>
      ) : '—' },
    { title: 'Action', key: 'action', width: 100,
      render: (_, r) => (
        <Button size="small" onClick={() => navigate(`/hr/assessments/${r.id}`)}>Open</Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="My Assessments"
        subtitle="Assessments where you are the appraisee, reporting authority, or reviewing authority"
        extra={<Button onClick={() => refetch()}>Refresh</Button>}
      />
      {rows.length === 0 ? (
        <Empty description="No assessments. Wait for HR to launch a cycle." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={rows}
               loading={isLoading} pagination={{ pageSize: 25 }} />
      )}
    </>
  )
}
